// A one-shot line for the sign-in page saying why the dashboard signed the user out:
// - a session the backend no longer accepts: a 401 whose body `code` is `mfa_required` (the company now requires
//   two-step sign-in and this session did not pass it), `password_sign_in_disabled` (it signs in with SSO only) or
//   `tenant_closed` (the company's TourOptima service has ended; neutral words, never the company's name)
//   — apiv2/dashboard_mfa.openapi.yaml DashboardSessionRefused;
// - the Sign-in security page turning two-step sign-in off, which ends every session.
// sessionStorage: it survives the sign-out's navigation in this tab, and is read (and dropped) once.

const KEY = 'cp_sign_out_reason';

export const SESSION_REFUSED_CODES = ['mfa_required', 'password_sign_in_disabled', 'tenant_closed'];

// A closed tenant's words (backend tenant_lifecycle NEUTRAL_MESSAGE) when an answer has no `detail`: the session
// refusal, the sign-in's 403 and the sign-in page's tenant card. Never the company's name or logo.
export const TENANT_CLOSED_TEXT = 'This service is no longer available.';

const DEFAULT_TEXT = {
    mfa_required: 'Your company requires two-step sign-in. Please sign in again.',
    password_sign_in_disabled: 'Your company signs in with single sign-on.',
    tenant_closed: TENANT_CLOSED_TEXT,
};

function storage() {
    try {
        return window.sessionStorage;
    } catch (_e) {
        return null;
    }
}

export function noteSignOutReason(text) {
    try {
        storage()?.setItem(KEY, text);
    } catch (_e) {
        // Blocked storage: the sign-in page just shows no reason.
    }
}

// For a 401 body: keeps the reason when it is a session refusal. True when it was one.
export function noteSessionRefusal(body) {
    const code = body && typeof body.code === 'string' ? body.code : null;
    if (!SESSION_REFUSED_CODES.includes(code)) {
        return false;
    }
    noteSignOutReason((typeof body.detail === 'string' && body.detail) || DEFAULT_TEXT[code]);
    return true;
}

// The reason, if any (null when there is none). A page reads it while rendering and clears it in an effect:
// StrictMode renders twice in development, and a read that removed it would lose it to the discarded render.
export function peekSignOutReason() {
    try {
        return storage()?.getItem(KEY) || null;
    } catch (_e) {
        return null;
    }
}

export function clearSignOutReason() {
    try {
        storage()?.removeItem(KEY);
    } catch (_e) {
        // ignore
    }
}

// The reason, once.
export function takeSignOutReason() {
    const text = peekSignOutReason();
    clearSignOutReason();
    return text;
}
